import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styles from './checkDetails.module.scss';
import { LABEL } from '@veriables';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import cn from 'classnames';
import { safeJsonParse, getHoldTimerState } from '@utility/helperFunction';
import { HoldTimerBadge } from '@components/HoldController/HoldTimerBadge';
import HoldExpiredMessage from '@components/checksCommon/HoldExpiredMessage';
import { ReactComponent as RefillIcon } from '@assets/images/RefillIcon.svg';

dayjs.extend(utc);
dayjs.extend(duration);

const GRID = {
  gridTemplateColumns: '10% auto 12% 12% 12%',
};

// Priority constants for item sorting: Sent=0, Pending=1, Hold=2
const SORT_PRIORITY = {
  sent: 0,
  pending: 1,
  hold: 2,
};

const getItemSortPriority = item => {
  if (!item.isPending) return SORT_PRIORITY.sent;
  if (item.sendDate) return SORT_PRIORITY.hold;
  return SORT_PRIORITY.pending;
};

const getSeatLabel = (item, seats) => {
  if (item?.seats?.length > 0) {
    if (item.seats.length > 1) {
      return item.seats[0]?.tableOrder ? 'T' : 'S';
    }
    const seatId = item.seats[0]?.seatId;
    return seats?.find(s => s.id === seatId)?.number ?? '';
  }
  if (item?.seatIds?.length >= 0) {
    if (item.seatIds.length === 0) return 'T';
    if (item.seatIds.length > 1) return 'S';
    const seatId = item.seatIds[0];
    return seats?.find(s => s.id === seatId)?.number ?? '';
  }
  return '';
};

// Isolated row so only that row re-renders when its hold timer elapses
const CheckItemRow = ({ item, checkData, serverAppTheme, menuItemsHashTable }) => {
  const isHeld = item.isPending && !!item.sendDate;

  const isRefill = !!item.refill;
  const refillMenuItemId = menuItemsHashTable?.[item?.menuItemId]?.refillMenuItemId;
  const refillItem = refillMenuItemId ? menuItemsHashTable?.[refillMenuItemId] : null;
  const displayName = isRefill ? (refillItem?.name || item?.menuItemName) : item?.menuItemName;

  const [isElapsed, setIsElapsed] = useState(
    () => isHeld && !getHoldTimerState(item.sendDate)?.isPositive
  );

  useEffect(() => {
    if (!isHeld || isElapsed) return;
    const id = setInterval(() => {
      if (!getHoldTimerState(item.sendDate)?.isPositive) {
        setIsElapsed(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isHeld, item.sendDate, isElapsed]);

  const rowBg = isHeld
    ? isElapsed
      ? 'rgb(245, 176, 176)'
      : 'rgb(255, 225, 204)'
    : item.isPending
      ? serverAppTheme?.pending_items
      : '#ededed';

  const renderModifiers = (modifiers, depth = 0) => {
    if (!modifiers || !Array.isArray(modifiers)) return null;
    return modifiers.map((mod, idx) => (
      <React.Fragment key={`${depth}-${mod.modifierId || idx}`}>
        <div />
        <div
          style={{
            paddingLeft: depth > 0 ? `${depth * 10}px` : '0px',
            fontSize: '12px',
            color: '#787878',
            paddingTop: '3px',
          }}
        >
          {mod.quantity > 1 ? `- ${mod.quantity} x ` : '- '}{mod.modifierName}
        </div>
        <div />
        <div />
        <div style={{ fontSize: '12px', color: '#787878', paddingTop: '3px' }}>
          {LABEL().CURRENCY_SYMBOL}{(mod.price || 0).toFixed(2)}
        </div>
        {renderModifiers(mod.modifiers, depth + 1)}
      </React.Fragment>
    ));
  };

  return (
    <div
      key={item.id}
      style={{ ...GRID, background: rowBg }}
      className={cn(styles.table, styles.tableRow, 'my-1 rounded')}
    >
      <div>{item.quantity ?? ''}</div>
      <div>
        <div className="d-flex align-items-center">
          {item.isDiscount ? item.discountName : displayName}
          {!item.isDiscount && isRefill && (
            <RefillIcon
              fill={serverAppTheme?.iconcolor}
              width="14"
              height="14"
              className="ml-1"
              alt="refill"
            />
          )}
        </div>
        {isHeld && (
          <HoldTimerBadge
            sendDate={item.sendDate}
            serverAppTheme={serverAppTheme}
          />
        )}
        {isHeld && (
          <HoldExpiredMessage
            sendDate={item.sendDate}
            message={item.message}
            iconClassName="pb-0 pr-1"
             messageClassName={styles.paddingTop}
          />
        )}
      </div>
      <div className={styles.textAlign}>
        <span className={styles.seatBG}>{getSeatLabel(item, checkData?.seats)}</span>
      </div>
      <div>
        {item.sentAt && !isNaN(new Date(item.sentAt).getTime())
          ? dayjs.utc(item.sentAt).local().format('HH:mm')
          : '-'}
      </div>
      <div>
        {item.isDiscount
          ? `-${LABEL().CURRENCY_SYMBOL}${item.value?.toFixed(2)}`
          : `${LABEL().CURRENCY_SYMBOL}${(isRefill ? 0 : (item.price ?? 0)).toFixed(2)}`}
      </div>
      {renderModifiers(item.modifiers)}
    </div>
  );
};

// ── CheckDetails component ────────────────────────────────────────────────────

const CheckDetails = ({
  serverAppTheme = {},
  isTab = false,

  tabName = '',
  stationName = '',
  openDate = null,
  totalAmount = 0,

  modalNumber = null,
  paidAmount = 0,
  closeDate = null,

  employeeFirstName = '',
  employeeLastName = '',
  unpaidAmount = 0,

  checkOpen = true,
  checkData = {},
  menuItemsHashTable,
}) => {
  const openLocalTime = openDate
    ? dayjs
      .utc(openDate)
      .local()
      .format('HH:mm')
    : '';

  const closeLocalTime = closeDate
    ? dayjs
      .utc(closeDate)
      .local()
      .format('HH:mm')
    : '';

  const now = dayjs.utc();
  const openDateFn = openDate ? dayjs.utc(openDate) : null;
  const closeDateFn = closeDate ? dayjs.utc(closeDate) : null;

  const durationValue = openDateFn
    ? checkOpen
      ? dayjs.duration(now.diff(openDateFn))
      : dayjs.duration(closeDateFn?.diff(openDateFn))
    : dayjs.duration(0);

  const readable = [
    String(Math.floor(durationValue.asHours())).padStart(2, '0'),
    String(durationValue.minutes()).padStart(2, '0'),
  ].join(':');

  const seat = safeJsonParse(window.sessionStorage.getItem('seat'), {});

  const rawItems = useMemo(
    () => [
      ...(checkData?.discounts || []).map(item => ({
        ...item,
        isDiscount: true,
      })),
      ...(checkData?.Items?.length > 0 ? checkData.Items : checkData?.items || []).map(item => ({ ...item, isPending: false })),
      ...(checkData?.PendingItems || seat?.PendingItems || []).map(item => ({
        ...item,
        isPending: true,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkData]
  );

  // Sort items by priority: Sent (0) → Pending (1) → Hold (2)
  const items = useMemo(
    () =>
      [...rawItems].sort(
        (a, b) => getItemSortPriority(a) - getItemSortPriority(b)
      ),
    [rawItems]
  );

  return (
    <div className="col-12 px-2 h-100 d-flex flex-column">
      <div className="row">
        <div className="col-4 text-start">
          <div className={cn(styles.header, 'text-truncate')}>
            {isTab ? LABEL().TAB : LABEL().TABLE}:{' '}
            {isTab ? tabName : stationName}
          </div>
          <div>Opened at : {openLocalTime}</div>
          <div>
            Total : {LABEL().CURRENCY_SYMBOL}
            {totalAmount}
          </div>
        </div>
        <div className="col-4 text-center">
          <div className={styles.header}>{modalNumber}</div>
          <div>Closed at : {closeLocalTime}</div>
          <div>
            Paid : {LABEL().CURRENCY_SYMBOL}
            {paidAmount.toFixed(2)}
          </div>
        </div>
        <div className="col-4 d-flex flex-column align-items-end">
          <div className={cn(styles.header, 'text-truncate')}>
            Employee : {employeeFirstName ?? employeeLastName}
          </div>
          <div>Duration : {readable}</div>
          <div>
            Unpaid : {LABEL().CURRENCY_SYMBOL}
            {unpaidAmount.toFixed(2)}
          </div>
        </div>
      </div>
      <div className={cn(styles.table, styles.tableHeader)} style={GRID}>
        <div>Qty</div>
        <div>Items</div>
        <div className={styles.textAlign}>Seat</div>
        <div>Sent at</div>
        <div>Amount</div>
      </div>
      <div
        className="overflowY"
        style={{ flex: '1 1 auto', minHeight: 0 }}
      >
        {items.map(item => (
          <CheckItemRow
            key={item.id}
            item={item}
            checkData={checkData}
            serverAppTheme={serverAppTheme}
            menuItemsHashTable={menuItemsHashTable}
          />
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = ({ orders }) => ({
  menuItemsHashTable: orders.menuItemsHashTable,
});

export default connect(mapStateToProps)(CheckDetails);
